import {
  cond,
  equals,
  T,
  always,
  isEmpty,
  replace,
  toLower,
  toUpper,
  flatten,
  call,
  match,
  pipe,
  join,
  take,
  toString,
  trim,
  slice,
  last
} from 'ramda';
import { generateRandomEmail, phoneNumberFormatter } from 'src/utilities/borrowers';
import { stringFormatter } from 'src/features/Users/Works/Work/Explanation/loe_template';
import {
  formatCurrencyFn,
  formatCurrencyWithPrecisionFn,
  commaSeperatedFormatter,
  formatRoundedCurrencyFn,
  unFormatCurrencyFnNum,
  unFormatCurrencyFn,
  unFormatCurrentPrecisionFn,
  unFormatCurrencyFnWithEmptyStringSupport,
  integerStringToDecimalFormatter,
} from 'src/utilities/formatCurrency';
import {
  ssnFormatter,
  phoneNumberOrNA,
  isoTinFormattorFn,
  itinFormatterFn
} from 'src/utilities/formatTin';
import {
  dateFormatterFn,
  dateFormatterWithDash,
  dateWithTimeStamp,
  humanizeUTCDateWithoutTimeZone
} from 'src/utilities/formatDate';
import { isNotEmpty } from 'src/utilities/hasValue';
import { addressStringified, isCountryNotUS } from 'src/utilities/addressJoiner';
import { isBoolean, isString } from 'src/utilities/types';
import { DOCUMENT_REPRESENTS_1099 } from 'src/services/constants';
import { DOCUMENT_REPRESENTS_W2 } from 'src/components/CheckLists/documents';
import { firstName, getFullName } from 'src/utilities/naming';
import { lengthFromListOrStringSeperatedBy } from 'src/components/convertor';
import { capitalizeAllWords } from 'src/utilities/capitalize';
import { TRUE } from 'src/constants';
import { normalizeNumber } from 'src/utilities/formatNumbers';

export const getFormatters = (key: string) => {
  const result = cond([
    [equals('PHONE_NUMBER_FORMATTER'), always(phoneNumberFormatter)],
    [equals('PHONE_NUMBER_WITH_NA_FORMATTER'), always(phoneNumberOrNA)],
    [equals('INTEGER_STRING_TO_DECIMAL_STRING'), always(integerStringToDecimalFormatter)],
    [equals('CURRENCY_FORMATTER'), always(formatCurrencyFn)],
    [equals('CURRENCY_FORMATTER_PRECISION'), always(formatCurrencyWithPrecisionFn)],
    [equals('ROUNDED_CURRENCY_FORMATTER'), always(formatRoundedCurrencyFn)],
    [equals('ISOEIN_FORMATTER'), always(isoTinFormattorFn)],
    [equals('ITIN_FORMATTER'), always(itinFormatterFn)],

    [equals('SSN_FORMATTER'), always(ssnFormatter)],
    //validate formatted date with slash like 10/10/2021 (eg) if we enter wrong format it will return undefined
    [equals('DATE_FORMATTER'), always(dateFormatterFn)],
    //format the date using humanizeUTCDateWithoutTimeZone (eg) if we have 20 APR 2022 as date it will convert them into 04/20/2022
    [equals('DATE_FORMATTER_URLA'), always(humanizeDateFormatterURLAfn)],
    [equals('DATE_FORMATTER_WITH_TIME'), always(dateWithTimeStamp)],
    // convert dates with slashes into dashes (eg) 10/04/2020 -> 10-04-2020
    [equals('DATE_FORMATTER_DASH'), always(dateFormatterWithDash)],
    [equals('PERCENT_FORMATTER_WITHOUT_SYMBOL'), always(percentFormatFn())],
    [equals('NUMBER_FORMATTER'), () => numberFormatFn()],
    //conver the given value to Number
    [equals('NUMBER_ONLY_FORMATTER'), () => numberOnlyFormatter],
    [equals('ADDRESS_STRINGIFY'), always(addressStringified)],
    [equals('YES_NO_FORMATTER_UPPER'), always(yesNoFormatterUpper)],
    [equals('YES_NO_FORMATTER_LOWER'), always(yesNoFormatterLower)],
    [equals('YES_NO_FORMATTER_UPPER_REQUIRED'), always(yesNoFormatterUpperRequired)],
    [equals('NO_YES_FORMATTER_UPPER'), always(noYesFormatterUpper)],
    [equals('HAS_ANY_VALUE_TO_YES_NO'), always(hasAnyValueYesNoFormatterUpper)],
    [equals('YES_NO_TO_BOOLEAN_FORMATTER'), always(yesNoToBooleanFormatter)],
    [equals('NO_YES_TO_BOOLEAN_FORMATTER'), always(noYesToBooleanFormatter)],
    [equals('YES_NO_TO_BOOLEAN_STRING_FORMATTER'), always(yesNoToBooleanStringFormatter)],
    [equals('BOOL_STRING_TO_YES_NO_FORMATTER'), always(boolStringToYesNo)],
    [equals('TOTAL_MONTHS_TO_YEARS'), always(totalMonthsToYears)],
    [equals('TOTAL_MONTHS_TO_REMAINING_MONTHS'), always(totalMonthsToRemainingMonth)],
    [equals('IS_COUNTRY_NOT_US'), always(isCountryNotUS)],
    [equals('CURRENCY_TO_NUMBER_FORMATTER'), always(unFormatCurrencyFnNum)],
    [equals('CURRENCY_TO_STRING_FORMATTER'), always(unFormatCurrencyFn)],
    [equals('CURRENCY_TO_STRING_PRECISION_FORMATTER'), always(unFormatCurrentPrecisionFn)],
    [equals('CURRENCY_TO_STRING_EMPTY_FORMATTER'), always(unFormatCurrencyFnWithEmptyStringSupport)],
    [equals('STRING_TO_BOOLEAN_FORMATTER'), always(stringToBool)],
    [equals('APPLICABLE_FORMATTER'), () => isEmptyFormatter],
    [equals('EMPLOYMENT_TYPE_TO_BOOLEAN'), () => employmentTypeToBoolean],
    [equals('BOOLEAN_TO_EMPLOYMENT_TYPE'), () => booleanToEmploymentType],
    [equals('STRING_FORMATTER'), always(stringFormatter)],
    [equals('CHECKBOX_FORMATTER'), () => doNothing()],
    [equals('DO_NOTHING'), () => doNothing()],
    [equals('FLATTEN'), always((a: any) => flatten([a]))],
    [equals('FULL_NAME'), always(getFullName)],
    [equals('FIRST_NAME'), always(firstName)],
    [equals('FUNCTION_CALL'), always(call)],
    [equals('COMMA_SEPERATED_FORMATTER'), always(commaSeperatedFormatter)],
    [equals('YEAR_FORMATTER'), always(sliceDigits)],
    [equals('lengthFromListOrCommaString'), always(lengthFromListOrStringSeperatedBy(','))],
    [equals('NUMBER_FORMATTER_LIMIT'), ()=>(data:any) => numberLimitFormatFn(data,9)],
    [equals('JSON_FORMATTER'), always((data: string) => JSON.parse(data))],
    [equals('CAPITALIZE'), always((data: string) => capitalizeAllWords(toLower(trim(data))))],
    [equals('UPPER_CASE'), always((data: string) => toUpper(trim(data)))],
    [equals('LENGTH_FROM_LIST_OR_COMMA_SEPERATED_BY'), always(lengthFromListOrStringSeperatedBy(','))],
    [equals('GENERATE_RANDOM_EMAIL'), always(generateRandomEmail)],
    [T, always(stringFormatter)],
  ])(key);
  return result
};

export const humanizeDateFormatterURLAfn = (date: string) => {
  if (isNotEmpty(date)) {
    return humanizeUTCDateWithoutTimeZone(date, { format: "MM/DD/YYYY" });
  }
  return date;
};

const numberFormat = (value: any) => {
  if (!isNaN(Number(value))) {
    return value;
  }
  return replace(/[^\d\.]*/g, '', value);
};

export const numberFormatFn = () => {
  return numberFormat
};

export const numberLimitFormatFn = (data:any,limit:any) => {
  return pipe(
    toString,
    match(/\d/g),
    take(limit),
    join('')
  )(data)
};



export const numberOnlyFormatter = (value: any) => {
  return Number(numberFormat(value))
}


/**
 * Slices the first `limit` digits from a string or number.
 *
 * @param value The string to be sliced.
 * @param limit The number of digits to slice. Defaults to 4.
 * @returns The sliced string.
 *
 * @example
 * sliceDigits('1234567890', 4) // => 1234
 * sliceDigits(1234567, 5) // => 12345
 */
const sliceDigits = (value: any, limit: number = 4) => {
  const result = pipe(
    toString,
    match(/\d/g),
    take(limit),
    join(''),
    Number
  )(value)
  return result
}



export const doNothing = () => (value: any) => value;

export const percentFormatFn = () => {
  return (value: any) => {
    if (Number(value) <= 100 && value !== ' ') {
      return isEmpty(value) ? 0 : parseFloat(value);
    }
    return 0;
  };
};

const boolToYesNo = (value: boolean) => value ? 'Yes' : 'No'

const boolStringToYesNo = (value: string) => equals(toLower(value), TRUE) ? 'Yes': 'No'
/**
 public method: yesNoFormatter
 this function is to convert given value to yes or no
 * @param value { any } value can be any type
 * returns string 'YES' if given value has value other vise return 'NO'
*/

export const yesNoFormatterUpper = (value: any) => {
  if (isString(value) && isEmpty(value)) {
    return value
  }

  if (isBoolean(value)) {
    return boolToYesNo(value)
  }
  return value && equals(toLower(`${value}`), 'yes') ? "Yes" : "No"
}

export const yesNoFormatterUpperRequired = (value:any) => {
  return yesNoFormatterUpper(value)
}

/**
 public method: yesNoFormatter
 this function is to convert given value to yes or no
 * @param value { any } value can be any type
 * returns if given value has value string is 'yes' other vise return 'no'
*/

export const yesNoFormatterLower = (value:any) => {
  return toLower(yesNoFormatterUpper(value))
}


const boolToNoYes = (value: boolean) => value ? 'No' : 'Yes'

export const noYesFormatterUpper = (value: any) => {
  if (isBoolean(value)) {
    return boolToNoYes(value)
  }
  return value && equals(toLower(value), 'no') ? "No" : "Yes"
}

export const hasAnyValueYesNoFormatterUpper = (value: any) => {
  if (isBoolean(value)) {
    return boolToYesNo(value)
  }
  return isNotEmpty(value) ? "Yes" : "No"
}


/**
 public method: yesNoToBooleanFormatter
 this function is to convert given value to true or false
 * @param value { any } value should be yes or no or true or false
 * returns boolean true if given value is 'yes' or true other vise return false
*/

const yesNoToBooleanFormatter = (value: any) => {
  return equals(value, true) || equals(toLower(`${value}`), 'yes')
}

const yesNoToBooleanStringFormatter = (value: string) => {
  return `${equals(toLower(value), 'yes')}`
}

const noYesToBooleanFormatter = (value: any) => {
  return equals(value, true) || isBoolean(value) ? value : equals(toLower(value), 'no')
}

const totalMonthsToYears = (months: any) => {
  return Math.floor(Number(numberFormat(months)) / 12)
}

const totalMonthsToRemainingMonth = (months: any) => {
  return Number(Number(numberFormat(months)) % 12)
}

export const stringToBool = (value: string) => {
  return isString(value) ? equals(toLower(value), 'true') : equals(value as any, true)
}


export const isEmptyFormatter = (value: any) => {
  return !isNotEmpty(value)
}

export const employmentTypeToBoolean = (value: string) => {
  return equals(DOCUMENT_REPRESENTS_1099, value)
}

export const booleanToEmploymentType = (value: boolean) => {
  return value ? DOCUMENT_REPRESENTS_1099 : DOCUMENT_REPRESENTS_W2
}


export const handleUnformattedValue = (isDecimal: boolean, rawValue: string) => {
  let formattedValue = rawValue;
  /**
   * @description currency formatter precision return value as it is
   * when the value is ending with . i.e 2000. , 30. etc, this is needed since on formatting
   * ending decimal point will be ignored making it impossible for the user to type in decimals
   *
   * in this particular use case we strip of all formatting like dollars, comma etc. so when 2000. is passed
   * it'll return 2000., which should not be the case so we're pre formatting it so it'll look like $2,000.
   */
  if (isDecimal && equals(last(rawValue), '.')) {
    formattedValue = getFormatters('CURRENCY_FORMATTER')(
      slice(0, -1, formattedValue),
    );
    formattedValue += '.';
  }

  return `${getFormatters(isDecimal ? 'CURRENCY_FORMATTER_PRECISION' : 'CURRENCY_FORMATTER')(formattedValue)}`;
};

/**
 * Adds +1 country code to US phone numbers if not already present.
 */
export const formatPhoneNumberWithCountryCode = (phoneNumber: string): string => {
  const normalized = normalizeNumber(phoneNumber); // removes spaces, dashes, etc.

  // If already starts with +1 or 1 followed by 10 digits, keep it
  if (/^\+?1\d{10}$/.test(normalized)) {
    return normalized.startsWith('+') ? normalized : `+${normalized}`;
  }

  // If only 10 digits (assumed to be US), add +1
  if (/^\d{10}$/.test(normalized)) {
    return `+1${normalized}`;
  }

  // For invalid formats, fallback to just `+` plus normalized number
  return `+${normalized}`;
};
